'use client';

import { BadgeCheck, Building2, CheckCircle2, Home, MapPin, PiggyBank, RotateCcw, ShieldCheck, SlidersHorizontal, Star, WalletCards } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'students-housing-option-compare-v1';

const defaultOptions = [
  {
    id: 'dorm',
    name: 'Общежитие рядом с вузом',
    monthlyCost: '6000',
    commuteMinutes: '15',
    firstPayment: '6000',
    safety: '4',
    condition: '3',
    documentsChecked: true,
  },
  {
    id: 'room',
    name: 'Комната с соседями',
    monthlyCost: '18000',
    commuteMinutes: '30',
    firstPayment: '36000',
    safety: '3',
    condition: '4',
    documentsChecked: false,
  },
  {
    id: 'flat',
    name: 'Квартира целиком',
    monthlyCost: '32000',
    commuteMinutes: '25',
    firstPayment: '64000',
    safety: '3',
    condition: '4',
    documentsChecked: false,
  },
] as const;

type HousingOption = {
  id: string;
  name: string;
  monthlyCost: string;
  commuteMinutes: string;
  firstPayment: string;
  safety: string;
  condition: string;
  documentsChecked: boolean;
};

function parseNumber(value: string) {
  return Number(value.replace(/[^0-9]/g, '') || 0);
}

function clamp(value: number, min = 0, max = 100) {
  return Math.min(max, Math.max(min, value));
}

function formatMoney(value: number) {
  return new Intl.NumberFormat('ru-RU').format(Math.max(0, Math.round(value)));
}

function calculateScore(option: HousingOption, cheapestMonthly: number) {
  const monthly = parseNumber(option.monthlyCost);
  const firstPayment = parseNumber(option.firstPayment);
  const commute = parseNumber(option.commuteMinutes);
  const safety = clamp(Number(option.safety || 0), 1, 5);
  const condition = clamp(Number(option.condition || 0), 1, 5);

  const costScore = cheapestMonthly > 0 ? clamp(30 - ((monthly - cheapestMonthly) / cheapestMonthly) * 18, 4, 30) : 18;
  const commuteScore = clamp(24 - commute * 0.45, 3, 24);
  const safetyScore = safety * 7;
  const conditionScore = condition * 5;
  const documentsScore = option.documentsChecked ? 12 : -8;
  const prepayPenalty = monthly > 0 && firstPayment > monthly * 2 ? 8 : 0;

  return Math.round(clamp(costScore + commuteScore + safetyScore + conditionScore + documentsScore - prepayPenalty));
}

function readOptions(): HousingOption[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [...defaultOptions];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [...defaultOptions];
  } catch {
    return [...defaultOptions];
  }
}

function getVerdict(score: number) {
  if (score >= 78) return 'Сильный вариант';
  if (score >= 60) return 'Можно рассмотреть';
  if (score >= 45) return 'Нужна проверка';
  return 'Рискованный вариант';
}

export default function HousingOptionCompare() {
  const [options, setOptions] = useState<HousingOption[]>([...defaultOptions]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setOptions(readOptions());
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (!isReady) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
  }, [options, isReady]);

  const scoredOptions = useMemo(() => {
    const monthlyValues = options.map((option) => parseNumber(option.monthlyCost)).filter(Boolean);
    const cheapestMonthly = monthlyValues.length > 0 ? Math.min(...monthlyValues) : 0;

    return options
      .map((option) => ({
        ...option,
        score: calculateScore(option, cheapestMonthly),
        monthly: parseNumber(option.monthlyCost),
        firstPayment: parseNumber(option.firstPayment),
        commute: parseNumber(option.commuteMinutes),
      }))
      .sort((a, b) => b.score - a.score);
  }, [options]);

  const bestOption = scoredOptions[0];

  function updateOption(id: string, key: keyof HousingOption, value: string | boolean) {
    setOptions((current) =>
      current.map((option) =>
        option.id === id
          ? {
              ...option,
              [key]: typeof value === 'string' && key !== 'name' ? value.replace(/[^0-9]/g, '') : value,
            }
          : option,
      ),
    );
  }

  function resetOptions() {
    setOptions([...defaultOptions]);
  }

  return (
    <section className="mt-8 rounded-[32px] border border-main/25 bg-gradient-to-br from-slate-900/55 via-slate-900/40 to-sky-950/55 p-5 shadow-[0_24px_70px_-36px_rgba(250,204,21,0.28)] backdrop-blur-xl sm:p-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-main/35 bg-main/10 px-3 py-1 text-xs font-black text-main">
            <SlidersHorizontal className="h-4 w-4" />
            Сравнение вариантов
          </div>
          <h2 className="mt-4 text-2xl font-black text-main sm:text-3xl">
            Сравни общежитие, комнату и квартиру по реальным критериям
          </h2>
          <p className="mt-2 text-sm leading-7 text-slate-100/90 sm:text-base dark:text-slate-200/90">
            Цена — не единственный фактор. Укажи стоимость, дорогу, первый платёж, состояние, безопасность и проверку документов.
            Таблица сохранится в браузере и поможет выбрать вариант спокойнее.
          </p>
        </div>

        <button
          type="button"
          onClick={resetOptions}
          className="inline-flex w-fit items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-slate-100 transition hover:border-main/50 hover:text-main"
        >
          <RotateCcw className="h-4 w-4" />
          Сбросить варианты
        </button>
      </div>

      {bestOption ? (
        <article className="mt-6 rounded-3xl border border-main/35 bg-main/10 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-black text-main">
                <Star className="h-4 w-4" />
                Лучший баланс сейчас
              </div>
              <h3 className="mt-2 text-xl font-black text-main">{bestOption.name}</h3>
              <p className="mt-1 text-sm leading-6 text-slate-100/90">
                Оценка: {bestOption.score}/100. Ежемесячно: {formatMoney(bestOption.monthly)} ₽. Первый платёж: {formatMoney(bestOption.firstPayment)} ₽. Дорога: {bestOption.commute} мин.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/25 px-4 py-3 text-3xl font-black text-main">
              {bestOption.score}
            </div>
          </div>
        </article>
      ) : null}

      <div className="mt-5 grid gap-4 xl:grid-cols-3">
        {scoredOptions.map((option) => (
          <article key={option.id} className="rounded-3xl border border-white/10 bg-white/5 p-4 transition hover:border-main/40 hover:bg-white/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <label className="text-xs font-black uppercase tracking-wide text-main">Название варианта</label>
                <input
                  value={option.name}
                  onChange={(event) => updateOption(option.id, 'name', event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/35 px-3 py-2 text-sm font-black text-slate-100 outline-none transition focus:border-main/50"
                />
              </div>
              <div className="shrink-0 rounded-2xl border border-white/10 bg-slate-950/30 px-3 py-2 text-center">
                <div className="text-2xl font-black text-main">{option.score}</div>
                <div className="text-[11px] font-semibold text-slate-100/70">из 100</div>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
              {[
                { key: 'monthlyCost', label: 'В месяц', icon: WalletCards, suffix: '₽' },
                { key: 'firstPayment', label: 'Первый платёж', icon: PiggyBank, suffix: '₽' },
                { key: 'commuteMinutes', label: 'Дорога', icon: MapPin, suffix: 'мин' },
                { key: 'safety', label: 'Безопасность 1–5', icon: ShieldCheck, suffix: '/5' },
                { key: 'condition', label: 'Состояние 1–5', icon: Building2, suffix: '/5' },
              ].map((field) => {
                const Icon = field.icon;
                return (
                  <label key={field.key} className="rounded-2xl border border-white/10 bg-slate-950/25 p-3">
                    <span className="flex items-center gap-2 text-xs font-semibold text-slate-100/75">
                      <Icon className="h-4 w-4 text-main" />
                      {field.label}
                    </span>
                    <span className="mt-2 flex items-center gap-2">
                      <input
                        inputMode="numeric"
                        value={option[field.key as keyof HousingOption] as string}
                        onChange={(event) => updateOption(option.id, field.key as keyof HousingOption, event.target.value)}
                        className="min-w-0 flex-1 bg-transparent text-base font-black text-slate-100 outline-none"
                      />
                      <span className="text-xs font-semibold text-slate-100/60">{field.suffix}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => updateOption(option.id, 'documentsChecked', !option.documentsChecked)}
              className={`mt-4 flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left text-sm font-semibold transition ${
                option.documentsChecked
                  ? 'border-main/45 bg-main/10 text-main'
                  : 'border-white/10 bg-white/5 text-slate-100 hover:border-main/40 hover:text-main'
              }`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                  option.documentsChecked ? 'border-main bg-main text-sky-950' : 'border-white/20 text-slate-100'
                }`}
              >
                <CheckCircle2 className="h-4 w-4" />
              </span>
              Документы и условия проверены
            </button>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-sm leading-6 text-slate-100/90">
              <div className="font-black text-main">{getVerdict(option.score)}</div>
              <div className="mt-1">
                Чем выше оценка, тем спокойнее вариант по сумме, дороге, документам, безопасности и состоянию.
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm leading-6 text-slate-100/90">
        <div className="flex gap-2">
          <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-main" />
          <span>
            Это не юридическая оценка, а студенческий фильтр здравого смысла. Перед оплатой всё равно проверь документы,
            договор, возврат залога и реальные условия проживания.
          </span>
        </div>
      </div>
    </section>
  );
}
